import React, { useEffect, useCallback } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useDashboard } from '../../../contexts/DashboardContext';
import { ChartCard, ChartGrid, CustomTooltip, CHART_COLORS, formatCurrency } from '../ChartComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RiskAssetQualityTab = () => {
  const { dataCache, setTabData, loading, setTabLoading, errors, setTabError, filters, isTabCached } = useDashboard();
  
  const data = dataCache.risk;

  const fetchData = useCallback(async () => {
    if (isTabCached('risk')) return;
    
    setTabLoading('risk', true);
    setTabError('risk', null);
    
    try {
      const params = new URLSearchParams();
      if (filters.asOnDate) params.append('as_on_date', filters.asOnDate);
      if (filters.branch) params.append('branch', filters.branch);
      
      const response = await fetch(`${API_URL}/api/dashboard/risk-asset-quality?${params}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const result = await response.json();
      setTabData('risk', result);
    } catch (err) {
      setTabError('risk', err.message);
    } finally {
      setTabLoading('risk', false);
    }
  }, [filters, isTabCached, setTabData, setTabLoading, setTabError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading.risk) {
    return (
      <ChartGrid>
        {[1, 2, 3, 4].map(i => <ChartCard key={i} title="" loading={true} />)}
      </ChartGrid>
    );
  }

  if (errors.risk) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600">Error loading data: {errors.risk}</p>
        <button onClick={fetchData} className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { charts } = data;
  
  // Asset class color mapping
  const assetClassColors = {
    'STD': CHART_COLORS.primary,
    'SMA': CHART_COLORS.warning,
    'DB1': '#f97316',
    'DB2': CHART_COLORS.danger,
    'DB3': '#dc2626',
  };

  return (
    <ChartGrid>
      {/* Chart 1: Asset Classification Distribution (Bar) */}
      <ChartCard title="Asset Classification Distribution" subtitle="Loan accounts by asset class">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.asset_classification}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(0)}Cr`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {charts.asset_classification.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={assetClassColors[entry.name] || CHART_COLORS.neutral} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Chart 2: DPD Bucket Analysis (Bar) */}
      <ChartCard title="DPD Bucket Analysis" subtitle="Days past due distribution">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.dpd_buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(0)}Cr`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {charts.dpd_buckets.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={index === 0 ? CHART_COLORS.primary : index < 3 ? CHART_COLORS.warning : CHART_COLORS.danger} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Chart 3: NPA vs Provision Coverage (Combo) */}
      <ChartCard title="NPA vs Provision Coverage" subtitle="NPA amount and provision coverage by class">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={charts.npa_provision}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/10000000).toFixed(1)}Cr`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#94a3b8" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-slate-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs">
                      <p className="font-medium mb-1">{label}</p>
                      {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }}>
                          {entry.name}: {entry.name.includes('%') ? `${entry.value}%` : formatCurrency(entry.value)}
                        </p>
                      ))}
                    </div>
                  );
                }
                return null;
              }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="npa_amount" fill={CHART_COLORS.danger} name="NPA Amount" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="coverage_percent" stroke={CHART_COLORS.secondary} strokeWidth={2} name="Coverage %" dot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      {/* Chart 4: Overdue Amount by Product (Horizontal Bar) */}
      <ChartCard title="Overdue Amount by Product" subtitle="Top products by overdue">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={charts.overdue_by_product} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} stroke="#94a3b8" width={80} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={CHART_COLORS.warning} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>
    </ChartGrid>
  );
};

export default RiskAssetQualityTab;
